import React from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import ReplayIcon from '@mui/icons-material/Replay';
import CellChipStatus from './CellChipStatus';
import MarkStateDropdownMAIQ from 'app/components/MarkStateDropdownMAIQ';

interface CellRunLogStatusWithRetryProps {
  params: any;
  onClick: (params: any, clickedElement?: string) => void | Promise<void>;
  children: React.ReactNode;
}

const CellRunLogStatusWithRetry = ({
  params,
  onClick,
  children,
}: CellRunLogStatusWithRetryProps) => {
  const statusValue = String(children ?? '')
    .trim()
    .toLowerCase();
  const isFailed = statusValue === 'failed';
  const isRetrying = Boolean(params?.row?.__retrying);
  const needsRecordRetry =
    Boolean(params?.row?.has_retryable_records) && !isFailed;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        width: '100%',
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 0.25,
          minWidth: 0,
        }}
      >
        <CellChipStatus>{children}</CellChipStatus>
        {needsRecordRetry && (
          <Tooltip title="Completed, with records that can be retried from the run details">
            <Chip
              size="small"
              label="Needs record retry"
              sx={{
                height: 16,
                fontSize: 10,
                backgroundColor: 'transparent',
                color: '#b45309',
                '& .MuiChip-label': { px: 0 },
              }}
            />
          </Tooltip>
        )}
      </Box>
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}
      >
        <Box
          sx={{
            width: 28,
            height: 28,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {isFailed && (
            <Tooltip title={isRetrying ? 'Retrying...' : 'Retry'}>
              <span>
                <IconButton
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation();
                    onClick(params, 'retry');
                  }}
                  disabled={isRetrying || Boolean(params?.row?.__markingState)}
                  aria-label="retry run log"
                  sx={{
                    color: '#b91c1c',
                    '&:hover': { backgroundColor: '#fef2f2' },
                  }}
                >
                  {isRetrying ? (
                    <CircularProgress size={16} thickness={5} />
                  ) : (
                    <ReplayIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>
        <MarkStateDropdownMAIQ
          entityId={`run-${params?.row?._id}`}
          disabled={isRetrying || Boolean(params?.row?.__markingState)}
          onChange={(state) => onClick(params, `mark-state-${state}`)}
        />
      </Box>
    </Box>
  );
};

export default CellRunLogStatusWithRetry;
